// Phase APP-3 · Founder Oversight page — read-only company-wide approval
// monitoring with the special Founder Override path.
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Loader2, RefreshCw, Eye, Shield, X, ChevronDown, ChevronRight, Filter } from "lucide-react";
import RoleGate from "../../components/RoleGate";
import useTimedFetch from "../../hooks/useTimedFetch";
import StatusBadge from "../../components/hub/StatusBadge";
import AdminApprovalsKPIs from "../../components/admin/approvals/AdminApprovalsKPIs";
import ApprovalDetailDrawer from "../../components/admin/approvals/ApprovalDetailDrawer";
import FounderOverrideModal from "../../components/admin/approvals/FounderOverrideModal";

const PURPLE = "#8B5CF6";
const PAGE_SIZE = 25;

const ALL_STATUSES = [
  "pending", "rejected_1", "resubmitted_pending",
  "approved", "rejected_terminal", "founder_override_approved",
];

function relTime(iso) {
  if (!iso) return "—";
  const t = new Date(iso).getTime();
  const diff = Math.max(0, Date.now() - t);
  const m = Math.floor(diff / 60_000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

function MultiSelect({ options, value, onChange, placeholder, testid }) {
  const v = value || [];
  const toggle = (opt) => {
    if (v.includes(opt)) onChange(v.filter((x) => x !== opt));
    else onChange([...v, opt]);
  };
  return (
    <details className="relative" data-testid={testid}>
      <summary className="list-none cursor-pointer border border-slate-200 rounded-lg px-2 py-1.5 text-xs flex items-center gap-1 select-none">
        <span className="flex-1 text-slate-700">
          {v.length === 0 ? (placeholder || "Any")
            : v.length === 1 ? v[0]
            : `${v.length} selected`}
        </span>
        <ChevronDown className="h-3 w-3 text-slate-400"/>
      </summary>
      <div className="absolute z-10 mt-1 left-0 right-0 bg-white border border-slate-200 rounded-lg shadow-lg max-h-56 overflow-y-auto">
        {options.length === 0
          ? <div className="px-3 py-1.5 text-xs text-slate-400">No options</div>
          : options.map((o) => (
              <label key={o} className="flex items-center gap-2 px-3 py-1.5 text-xs hover:bg-orange-50 cursor-pointer">
                <input type="checkbox"
                       checked={v.includes(o)}
                       onChange={() => toggle(o)}
                       className="h-3 w-3 accent-[#FF6B00]"/>
                <span className="truncate">{o}</span>
              </label>
            ))}
      </div>
    </details>
  );
}

function Th({ children, className = "", onClick, sortable }) {
  return (
    <th className={`px-3 py-2 font-semibold uppercase tracking-wider text-[10px] ${className} ${sortable ? "cursor-pointer hover:text-[#FF6B00]" : ""}`}
        onClick={onClick}>
      {children}
    </th>
  );
}

function FiltersBar({ value, onChange, options, isMobile, expanded, setExpanded }) {
  const set = (k, v) => onChange({ ...value, [k]: v });
  const clear = () => onChange({});
  const activeCount = [
    value.cities, value.main_hubs, value.micro_hubs,
    value.statuses, value.approval_type,
  ].filter((x) => x && (Array.isArray(x) ? x.length > 0 : true)).length;

  const inner = (
    <>
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 text-xs"
           data-testid="oversight-filters-grid">
        <MultiSelect options={options.cities || []} value={value.cities || []}
                       onChange={(v) => set("cities", v)}
                       placeholder="All cities" testid="filter-cities"/>
        <MultiSelect options={options.main_hubs || []} value={value.main_hubs || []}
                       onChange={(v) => set("main_hubs", v)}
                       placeholder="All main hubs" testid="filter-main-hubs"/>
        <MultiSelect options={options.micro_hubs || []} value={value.micro_hubs || []}
                       onChange={(v) => set("micro_hubs", v)}
                       placeholder="All micro hubs" testid="filter-micro-hubs"/>
        <select value={value.approval_type || ""}
                onChange={(e) => set("approval_type", e.target.value)}
                data-testid="filter-approval-type"
                className="border border-slate-200 rounded-lg px-2 py-1.5">
          <option value="">All types</option>
          <option value="micro_hub_suggestion">Micro hub suggestion</option>
          <option value="packer_request">Packer request</option>
        </select>
        <MultiSelect options={ALL_STATUSES} value={value.statuses || []}
                       onChange={(v) => set("statuses", v)}
                       placeholder="All statuses" testid="filter-statuses"/>
      </div>
      {activeCount > 0 && (
        <button onClick={clear} data-testid="oversight-clear-filters"
                className="mt-2 text-[11px] text-[#FF6B00] hover:underline inline-flex items-center gap-1">
          <X className="h-3 w-3"/> Clear all filters
        </button>
      )}
    </>
  );

  if (isMobile) {
    return (
      <div className="bg-white border border-slate-200 rounded-2xl">
        <button onClick={() => setExpanded((v) => !v)}
                data-testid="oversight-filters-toggle"
                className="w-full flex items-center gap-2 px-3 py-2.5">
          {expanded ? <ChevronDown className="h-4 w-4"/> : <ChevronRight className="h-4 w-4"/>}
          <Filter className="h-4 w-4 text-[#FF6B00]"/>
          <span className="text-sm font-semibold flex-1 text-left">Filters</span>
          {activeCount > 0 && (
            <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-orange-100 text-[#C2410C] font-semibold">
              {activeCount}
            </span>
          )}
        </button>
        {expanded && <div className="px-3 pb-3">{inner}</div>}
      </div>
    );
  }
  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-3 sticky top-0 z-10"
         data-testid="oversight-filters">
      {inner}
    </div>
  );
}

function Body() {
  const [searchParams, setSearchParams] = useSearchParams();
  const urlId = searchParams.get("id");

  const [filters, setFilters] = useState({});
  const [sortBy, setSortBy] = useState("submitted_at");
  const [page, setPage] = useState(0);
  const [mobileExpanded, setMobileExpanded] = useState(false);
  const [openRequestId, setOpenRequestId] = useState(urlId || null);
  const [overrideFor, setOverrideFor] = useState(null);
  const [isMobile, setIsMobile] = useState(false);

  // Mobile detection
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 640px)");
    setIsMobile(mq.matches);
    const onChange = (e) => setIsMobile(e.matches);
    mq.addEventListener?.("change", onChange);
    return () => mq.removeEventListener?.("change", onChange);
  }, []);

  // Fetch the queue. Founder hits /queue (founder scope returns all).
  // We do client-side filter on city/hub since the backend doesn't yet
  // support those filters on /queue; status & type ARE backend params.
  const queueParams = useMemo(() => {
    const p = {};
    if (filters.approval_type) p.approval_type = filters.approval_type;
    // statuses → pick one if there's exactly one (backend supports single)
    if ((filters.statuses || []).length === 1) p.status = filters.statuses[0];
    p.limit = 200;
    return p;
  }, [filters]);
  const queueQ = useTimedFetch("/approvals/queue", { params: queueParams });

  // Hub names for the main_hub_id chip — fetch from founder drill-down
  const mainHubsQ = useTimedFetch("/founder/main-hubs");

  const allRows = useMemo(() => queueQ.data?.rows || [], [queueQ.data]);

  // Derive filter option sets (client-side)
  const options = useMemo(() => {
    const cities = new Set();
    const mains = new Set();
    const micros = new Set();
    (mainHubsQ.data?.rows || []).forEach((h) => {
      if (h.city) cities.add(h.city);
      mains.add(h.hub_id);
    });
    allRows.forEach((r) => {
      if (r.main_hub_id) mains.add(r.main_hub_id);
      if ((r.entity_payload || {}).micro_hub_id)
        micros.add(r.entity_payload.micro_hub_id);
    });
    return {
      cities: [...cities].sort(),
      main_hubs: [...mains].sort(),
      micro_hubs: [...micros].sort(),
    };
  }, [allRows, mainHubsQ.data]);

  // Apply client-side filters (cities, main_hubs, micro_hubs, multi-status)
  const filteredRows = useMemo(() => {
    let rows = [...allRows];
    if ((filters.statuses || []).length > 1) {
      rows = rows.filter((r) => filters.statuses.includes(r.status));
    }
    if ((filters.main_hubs || []).length > 0) {
      rows = rows.filter((r) => filters.main_hubs.includes(r.main_hub_id));
    }
    if ((filters.micro_hubs || []).length > 0) {
      rows = rows.filter((r) => {
        const mh = (r.entity_payload || {}).micro_hub_id;
        return mh && filters.micro_hubs.includes(mh);
      });
    }
    if ((filters.cities || []).length > 0) {
      const mainHubByCity = new Set(
        (mainHubsQ.data?.rows || [])
          .filter((h) => filters.cities.includes(h.city))
          .map((h) => h.hub_id));
      rows = rows.filter((r) => mainHubByCity.has(r.main_hub_id));
    }
    rows.sort((a, b) => {
      const av = a[sortBy], bv = b[sortBy];
      return av > bv ? -1 : av < bv ? 1 : 0;
    });
    return rows;
  }, [allRows, filters, mainHubsQ.data, sortBy]);

  const totalFiltered = filteredRows.length;
  const pageRows = filteredRows.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);
  const totalPages = Math.ceil(totalFiltered / PAGE_SIZE) || 1;

  // Reset page if filters change
  useEffect(() => { setPage(0); }, [filters]);

  // Sync drawer state to URL
  useEffect(() => {
    if (openRequestId) setSearchParams({ id: openRequestId });
    else setSearchParams({});
  }, [openRequestId, setSearchParams]);

  // Sync URL → state (e.g. notification bell deep-link while page is mounted).
  // Deps on `urlId` only (not `openRequestId`) so a deliberate state clear
  // doesn't get reverted by a stale `searchParams` snapshot.
  useEffect(() => {
    if (urlId) setOpenRequestId(urlId);
    else setOpenRequestId(null);
  }, [urlId]);

  const onActionDone = useCallback(() => {
    queueQ.reload();
  }, [queueQ]);

  return (
    <div className="max-w-7xl mx-auto p-4 sm:p-6 space-y-4" data-testid="founder-oversight-page">
      <div>
        <div className="flex items-center gap-2 flex-wrap">
          <h1 className="text-2xl font-bold text-slate-900">Operations Oversight</h1>
          <span data-testid="oversight-readonly-badge"
                className="text-[10px] px-2 py-0.5 rounded-full bg-purple-100 text-purple-700 border border-purple-200 font-semibold">
            Founder view · read-only (except override)
          </span>
          <span data-testid="oversight-build-stamp"
                className="ml-auto text-[10px] font-mono text-slate-400">APP-1.0-r7</span>
        </div>
        <div className="text-xs text-slate-500 mt-0.5">
          Company-wide approval monitoring. Founder Override creates the entity and
          is permanently audit-logged.
        </div>
      </div>

      <AdminApprovalsKPIs/>

      <FiltersBar value={filters} onChange={setFilters}
                  options={options} isMobile={isMobile}
                  expanded={mobileExpanded} setExpanded={setMobileExpanded}/>

      {/* Queue table */}
      <div className="bg-white border border-slate-200 rounded-2xl overflow-x-auto" data-testid="oversight-queue-table-wrap">
        <table className="w-full text-sm min-w-[1000px]" data-testid="oversight-queue-table">
          <thead className="bg-slate-50/70">
            <tr className="text-left text-slate-500">
              <Th>Status</Th>
              <Th>Type</Th>
              <Th>Entity</Th>
              <Th>Submitted by</Th>
              <Th>Main hub</Th>
              <Th sortable onClick={() => setSortBy("submitted_at")}>
                Submitted ↓
              </Th>
              <Th>Resub</Th>
              <Th className="text-right">Actions</Th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {queueQ.loading && pageRows.length === 0 && (
              <tr><td colSpan={8} className="px-3 py-2">
                <div className="space-y-1" data-testid="oversight-loading">
                  {[0, 1, 2, 3].map((i) => <div key={i} className="h-10 bg-slate-100 rounded-md animate-pulse"/>)}
                </div>
              </td></tr>
            )}
            {queueQ.error && (
              <tr><td colSpan={8} className="px-3 py-4" data-testid="oversight-error">
                <div className="bg-rose-50 border border-rose-200 rounded-lg p-3 text-sm">
                  Couldn't load · {queueQ.error.message}{" "}
                  <button onClick={queueQ.reload} className="text-rose-700 underline">Retry</button>
                </div>
              </td></tr>
            )}
            {!queueQ.loading && pageRows.length === 0 && (
              <tr><td colSpan={8} className="px-3 py-8 text-center text-slate-400" data-testid="oversight-empty">
                No approvals match the current filters.
                {(filters.cities || filters.main_hubs || filters.micro_hubs
                   || filters.statuses || filters.approval_type) && (
                  <button onClick={() => setFilters({})}
                          className="ml-2 text-[#FF6B00] hover:underline">Clear all</button>
                )}
              </td></tr>
            )}
            {pageRows.map((r) => (
              <tr key={r.request_id}
                  data-testid={`oversight-row-${r.request_id}`}
                  className="hover:bg-orange-50/30">
                <td className="px-3 py-2"><StatusBadge status={r.status} size="sm"/></td>
                <td className="px-3 py-2 capitalize text-[11px] whitespace-nowrap">
                  {(r.approval_type || "").replace(/_/g, " ")}
                </td>
                <td className="px-3 py-2 font-medium">{r.display_name}</td>
                <td className="px-3 py-2">
                  <div>{r.submitted_by_name}</div>
                  <div className="text-[10px] text-slate-400">{r.submitted_by_role}</div>
                </td>
                <td className="px-3 py-2 font-mono text-[11px] text-slate-600">{r.main_hub_id || "—"}</td>
                <td className="px-3 py-2 text-xs" title={r.submitted_at}>
                  {relTime(r.submitted_at)}
                </td>
                <td className="px-3 py-2 text-center">{r.resubmission_count ?? 0}/1</td>
                <td className="px-3 py-2 text-right">
                  <div className="inline-flex items-center gap-1">
                    <button onClick={() => setOpenRequestId(r.request_id)}
                            data-testid={`oversight-view-${r.request_id}`}
                            className="text-[11px] inline-flex items-center gap-1 px-2 py-1 rounded-lg border border-slate-200 hover:bg-slate-50">
                      <Eye className="h-3 w-3"/> View
                    </button>
                    {["pending", "rejected_1", "resubmitted_pending"].includes(r.status) && (
                      <button onClick={() => setOverrideFor(r)}
                              data-testid={`oversight-override-${r.request_id}`}
                              className="text-[11px] font-semibold inline-flex items-center gap-1 px-2 py-1 rounded-lg text-white"
                              style={{ backgroundColor: PURPLE }}>
                        <Shield className="h-3 w-3"/> Override
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalFiltered > PAGE_SIZE && (
        <div className="flex items-center justify-center gap-2 text-xs text-slate-500" data-testid="oversight-pagination">
          <button onClick={() => setPage((p) => Math.max(0, p - 1))}
                  disabled={page === 0}
                  className="px-2 py-1 rounded border border-slate-200 disabled:opacity-40">
            ←
          </button>
          <span>Page {page + 1} / {totalPages} · {totalFiltered} rows</span>
          <button onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                  disabled={page >= totalPages - 1}
                  className="px-2 py-1 rounded border border-slate-200 disabled:opacity-40">
            →
          </button>
        </div>
      )}
      {totalFiltered <= PAGE_SIZE && totalFiltered > 0 && (
        <div className="text-center text-[11px] text-slate-400">
          Showing {totalFiltered} row{totalFiltered === 1 ? "" : "s"}
        </div>
      )}

      <ApprovalDetailDrawer
        open={!!openRequestId}
        requestId={openRequestId}
        onClose={() => setOpenRequestId(null)}
        founderMode
        onChanged={onActionDone}
      />
      <FounderOverrideModal
        open={!!overrideFor} onClose={() => setOverrideFor(null)}
        request={overrideFor} onDone={onActionDone}/>
    </div>
  );
}

export default function FounderOversight() {
  return (
    <RoleGate allow={["founder", "ceo"]}>
      <Body/>
    </RoleGate>
  );
}
